import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { progressAPI } from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, Play, Clock } from 'lucide-react';

// Format seconds to MM:SS
const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// Default thumbnail
const DEFAULT_THUMBNAIL = 'https://images.unsplash.com/photo-1579783902614-a3fb3927b6a5?w=400&h=300&fit=crop';

export const ContinueWatching = () => {
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchContinueWatching = async () => {
            try {
                const response = await progressAPI.getContinueWatching();
                setVideos(response.data);
            } catch (err) {
                console.error('Failed to fetch continue watching:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchContinueWatching();
    }, []);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-accent" />
            </div>
        );
    }

    if (videos.length === 0) {
        return null; // Don't show section if no videos to continue
    }

    return (
        <section className="mb-12" data-testid="continue-watching-section">
            <div className="flex items-center gap-2 mb-6">
                <Clock className="h-5 w-5 text-accent" />
                <h2 className="font-serif text-2xl font-semibold">Continue Watching</h2>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {videos.map((video) => (
                    <Card 
                        key={video.video_id} 
                        className="overflow-hidden hover:shadow-lg transition-shadow group"
                        data-testid={`continue-watching-${video.video_id}`}
                    >
                        <Link to={`/video/${video.video_id}`}>
                            <div className="aspect-video bg-secondary relative">
                                <img
                                    src={video.thumbnail || DEFAULT_THUMBNAIL}
                                    alt={video.title}
                                    className="w-full h-full object-cover"
                                    onError={(e) => e.target.src = DEFAULT_THUMBNAIL}
                                />
                                
                                {/* Play overlay */}
                                <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                    <div className="bg-accent rounded-full p-3">
                                        <Play className="h-6 w-6 text-white fill-white" />
                                    </div>
                                </div>
                                
                                {/* Progress bar overlay */}
                                <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/30">
                                    <div 
                                        className="h-full bg-accent"
                                        style={{ width: `${video.progress_percent}%` }}
                                    />
                                </div>
                                
                                {/* Time remaining badge */}
                                <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                                    {formatTime(video.duration_seconds - video.progress_seconds)} left
                                </div>
                            </div>
                            
                            <div className="p-4">
                                <h3 className="font-medium text-sm line-clamp-2 mb-1">
                                    {video.title}
                                </h3>
                                {video.instructor && (
                                    <p className="text-xs text-muted-foreground">
                                        {video.instructor}
                                    </p>
                                )}
                                <div className="flex items-center justify-between mt-2">
                                    <span className="text-xs text-muted-foreground">
                                        {video.progress_percent}% complete
                                    </span>
                                    <Button 
                                        variant="ghost" 
                                        size="sm" 
                                        className="h-7 text-xs text-accent hover:text-accent/80"
                                    >
                                        Resume
                                    </Button>
                                </div>
                            </div>
                        </Link>
                    </Card>
                ))}
            </div>
        </section>
    );
};

export default ContinueWatching;
